import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { User, LogOut, Globe, Menu, X, ChevronDown } from 'lucide-react';

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { language, setLanguage, t, labels } = useLanguage();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getDashboardRoute = () => {
    if (!user) return '/';
    switch (user.role) {
      case 'admin': return '/admin';
      case 'doctor': return '/doctor';
      case 'pharmacist': return '/pharmacist';
      case 'patient': return '/patient';
      default: return '/';
    }
  };

  const LanguageSwitch = ({ mobile }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className={`text-white hover:bg-[#1976d2] ${mobile ? 'w-full justify-start' : ''}`} data-testid="language-switch">
          <Globe size={18} className={language === 'ar' ? 'ml-2' : 'mr-2'} />
          {labels[language]}
          <ChevronDown size={14} className="ms-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => setLanguage('ar')} data-testid="lang-ar">العربية</DropdownMenuItem>
        <DropdownMenuItem onClick={() => setLanguage('en')} data-testid="lang-en">English</DropdownMenuItem>
        <DropdownMenuItem onClick={() => setLanguage('fr')} data-testid="lang-fr">Français</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <nav className="bg-gradient-to-r from-[#1e88e5] to-[#1565c0] shadow-lg">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src="/images/logo.png" alt="Tababa" className="h-10 w-10 rounded-full bg-white p-1" />
            <div className="text-2xl font-bold text-white">{t('طبابة', 'Tababa', 'Tababa')}</div>
          </Link>

          <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-white" data-testid="mobile-menu-button">
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <>
                <Link to={getDashboardRoute()}>
                  <Button variant="ghost" className="text-white hover:bg-[#1976d2]" data-testid="profile-button">
                    <User className={language === 'ar' ? 'ml-2' : 'mr-2'} size={18} />
                    {user.full_name}
                  </Button>
                </Link>
                <Button onClick={handleLogout} variant="ghost" className="text-white hover:bg-[#1976d2]" data-testid="logout-button">
                  <LogOut className={language === 'ar' ? 'ml-2' : 'mr-2'} size={18} />
                  {t('تسجيل الخروج', 'Logout', 'Déconnexion')}
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-white hover:bg-[#1976d2]" data-testid="login-button">
                    {t('تسجيل الدخول', 'Login', 'Connexion')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button variant="default" className="bg-white text-[#1e88e5] hover:bg-gray-100" data-testid="register-button">
                    {t('إنشاء حساب', 'Register', 'S\'inscrire')}
                  </Button>
                </Link>
              </>
            )}
            <LanguageSwitch />
          </div>
        </div>

        {menuOpen && (
          <div className="md:hidden pb-4">
            <div className="flex flex-col gap-2">
              {user ? (
                <>
                  <Link to={getDashboardRoute()} onClick={() => setMenuOpen(false)}>
                    <Button variant="ghost" className="w-full text-white hover:bg-[#1976d2] justify-start">
                      <User className={language === 'ar' ? 'ml-2' : 'mr-2'} size={18} />
                      {user.full_name}
                    </Button>
                  </Link>
                  <Button onClick={() => { handleLogout(); setMenuOpen(false); }} variant="ghost" className="w-full text-white hover:bg-[#1976d2] justify-start">
                    <LogOut className={language === 'ar' ? 'ml-2' : 'mr-2'} size={18} />
                    {t('تسجيل الخروج', 'Logout', 'Déconnexion')}
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMenuOpen(false)}>
                    <Button variant="ghost" className="w-full text-white hover:bg-[#1976d2] justify-start">
                      {t('تسجيل الدخول', 'Login', 'Connexion')}
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => setMenuOpen(false)}>
                    <Button variant="default" className="w-full bg-white text-[#1e88e5] hover:bg-gray-100">
                      {t('إنشاء حساب', 'Register', 'S\'inscrire')}
                    </Button>
                  </Link>
                </>
              )}
              <LanguageSwitch mobile />
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
